import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { fetchOrgCompanySettingsRow } from '../saas/lib/orgCompanySettings';
import { fetchOrgById, fetchTbSiteOrganization, setWorkspaceOrg, type SaasOrg } from '../saas/lib/saasOrg';
import { getActiveWorkspaceOrgId } from '../saas/lib/activeWorkspaceOrgId';
import { useSaasWorkspace } from '../saas/contexts/SaasWorkspaceContext';
import { getImpersonationWorkspaceOrgId } from '../utils/userImpersonationOverride';
import {
  getPlatformDefaultBranding,
  isSaasPlatform,
  resolveCompanyBranding,
  type CompanySettingsLike,
  type ResolvedCompanyBranding,
} from '../utils/companyBranding';
import { buildAiChatSupportContact, setRuntimeAiSupportContact } from '../utils/aiChatBranding';
import {
  clearErrorSurfaceTenantBranding,
  setErrorSurfaceTenantBranding,
} from '../utils/errorSurfaceBranding';
import { resolveTenantPublicOrigin, setRuntimeTenantPublicOrigin } from '../utils/tenantPublicOrigin';
import {
  fetchSaasMarketingPathDefaults,
  mergeSaasMarketingPathConfig,
  parseOrgMarketingPathsFromBranding,
  setRuntimeTenantMarketingPaths,
} from '../saas/lib/saasMarketingPaths';
import { resolveLoyaltyProgramName } from '../utils/loyaltyProgramName';

interface TenantBrandContextValue {
  branding: ResolvedCompanyBranding;
  loading: boolean;
  org: SaasOrg | null;
  /** Tenant-branded loyalty/rewards program name (config-driven, with fallback). */
  loyaltyProgramName: string;
  refresh: () => Promise<void>;
}

const TenantBrandContext = createContext<TenantBrandContextValue | undefined>(undefined);

export function TenantBrandProvider({ children }: { children: ReactNode }) {
  const { org: workspaceOrg, isReady: workspaceReady } = useSaasWorkspace();
  const [org, setOrg] = useState<SaasOrg | null>(null);
  const [settings, setSettings] = useState<CompanySettingsLike | null>(null);
  const [loyaltyProgramNameRaw, setLoyaltyProgramNameRaw] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadLoyaltyProgramName = useCallback(async (organizationId: string | null) => {
    if (!organizationId) {
      setLoyaltyProgramNameRaw(null);
      return;
    }
    try {
      const { data } = await supabase
        .from('loyalty_settings')
        .select('program_name')
        .eq('organization_id', organizationId)
        .maybeSingle();
      setLoyaltyProgramNameRaw((data?.program_name as string | null) ?? null);
    } catch {
      setLoyaltyProgramNameRaw(null);
    }
  }, []);

  const load = useCallback(async () => {
    if (!isSaasPlatform()) {
      setLoading(true);
      try {
        const tbOrg = await fetchTbSiteOrganization();
        setOrg(tbOrg);
        if (tbOrg) {
          const data = await fetchOrgCompanySettingsRow<CompanySettingsLike>(tbOrg.id, '*');
          setSettings(data);
          await loadLoyaltyProgramName(tbOrg.id);
        } else {
          setSettings(null);
          setLoyaltyProgramNameRaw(null);
        }
      } catch {
        setOrg(null);
        setSettings(null);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!workspaceReady) {
      return;
    }
    setLoading(true);
    try {
      let o = workspaceOrg;
      if (!o) {
        const pinned = getActiveWorkspaceOrgId();
        if (pinned) o = await fetchOrgById(pinned);
      }
      setOrg(o);
      if (o) {
        const impersonationOrgId = getImpersonationWorkspaceOrgId();
        // Never re-pin a stale chrome org (Eco-Luxe) over the impersonated tenant (Harbor).
        if (!impersonationOrgId || impersonationOrgId === o.id) {
          void setWorkspaceOrg(o.id).catch(() => {
            /* pin may already be set; still scope the read below */
          });
        }
        const data = await Promise.race([
          fetchOrgCompanySettingsRow<CompanySettingsLike>(o.id, '*'),
          new Promise<null>((resolve) => {
            window.setTimeout(() => resolve(null), 6000);
          }),
        ]);
        setSettings(data);
        void loadLoyaltyProgramName(o.id);
      } else {
        setSettings(null);
        setLoyaltyProgramNameRaw(null);
      }
    } catch {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceOrg, workspaceReady, loadLoyaltyProgramName]);

  useEffect(() => {
    load();
  }, [load]);

  const branding = useMemo(
    () =>
      isSaasPlatform()
        ? resolveCompanyBranding(settings, {
            orgName: org?.name,
            orgBranding: org?.branding,
          })
        : settings
          ? resolveCompanyBranding(settings, {
              orgName: org?.name,
              orgBranding: org?.branding,
            })
          : getPlatformDefaultBranding(),
    [settings, org],
  );

  useEffect(() => {
    setRuntimeAiSupportContact(buildAiChatSupportContact(branding));
    if (isSaasPlatform() && org) {
      setErrorSurfaceTenantBranding(branding);
      resolveTenantPublicOrigin({ organizationId: org.id }).then(setRuntimeTenantPublicOrigin);
      fetchSaasMarketingPathDefaults().then((platformPaths) => {
        setRuntimeTenantMarketingPaths(
          mergeSaasMarketingPathConfig(platformPaths, parseOrgMarketingPathsFromBranding(org.branding)),
        );
      });
    } else {
      clearErrorSurfaceTenantBranding();
      setRuntimeTenantPublicOrigin(null);
      setRuntimeTenantMarketingPaths(null);
    }
  }, [branding, org]);

  const loyaltyProgramName = useMemo(
    () =>
      resolveLoyaltyProgramName({
        programName: loyaltyProgramNameRaw,
        companyName: branding.companyName,
        isSaas: isSaasPlatform(),
      }),
    [loyaltyProgramNameRaw, branding.companyName],
  );

  const value = useMemo(
    () => ({ branding, loading, org, loyaltyProgramName, refresh: load }),
    [branding, loading, org, loyaltyProgramName, load],
  );

  return <TenantBrandContext.Provider value={value}>{children}</TenantBrandContext.Provider>;
}

export function useTenantBrand(): TenantBrandContextValue {
  const ctx = useContext(TenantBrandContext);
  if (!ctx) {
    return {
      branding: getPlatformDefaultBranding(),
      loading: false,
      org: null,
      loyaltyProgramName: resolveLoyaltyProgramName({ isSaas: isSaasPlatform() }),
      refresh: async () => {},
    };
  }
  return ctx;
}
